import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

import { ApiError, listProjectMembers, type ProjectMember } from '../api.js';

/**
 * The project's people, fetched ONCE for the whole page.
 *
 * WHY THIS EXISTS
 * ---------------
 * The project page loads eighteen sections, four of which independently asked
 * the server for the same member list on every page load — the team table, the
 * quality register, the snag register and the task form. Nothing was slow yet;
 * the fix named in the handoff is "a shared loader on the page, not a cache in
 * each section", and this is it.
 *
 * WHY THERE IS STILL A FALLBACK
 * -----------------------------
 * A section must keep working when it is rendered on its own — which is how
 * every screen test renders it, and how a future page might. So the hook
 * fetches for itself when no provider covers this project, and behaves exactly
 * as the four copies did before. The provider is an optimisation the sections
 * do not have to know about.
 *
 * THE FAILURE POSTURE IS DELIBERATE AND UNCHANGED
 * ----------------------------------------------
 * For a SECTION, a member list that cannot be read is an empty dropdown, never
 * an error: the list is context for a form, not the point of the screen. For
 * the TEAM TABLE it is different — a consultant who may open the project and
 * not read its staffing needs to be told which of the three it is, so that one
 * consumes the full state.
 */

export type MembersState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly members: readonly ProjectMember[] }
  /** The caller may open the project but not read its staffing list. */
  | { readonly status: 'not_permitted' }
  | { readonly status: 'failed'; readonly message: string };

interface MembersContextValue {
  readonly projectId: string;
  readonly state: MembersState;
  readonly reload: () => Promise<void>;
  /** After a write, the server hands back the new list — no second round trip. */
  readonly replace: (members: readonly ProjectMember[]) => void;
}

const MembersContext = createContext<MembersContextValue | null>(null);

async function fetchState(projectId: string): Promise<MembersState> {
  try {
    return { status: 'ready', members: await listProjectMembers(projectId) };
  } catch (caught) {
    // 404 counts as refused: a non-member gets 404 rather than 403 so the
    // project's existence is not confirmed (ADR-004).
    const denied = caught instanceof ApiError && (caught.status === 403 || caught.status === 404);
    return denied
      ? { status: 'not_permitted' }
      : { status: 'failed', message: 'Could not load the team.' };
  }
}

export function ProjectMembersProvider({
  projectId,
  children,
}: {
  readonly projectId: string;
  readonly children: ReactNode;
}): JSX.Element {
  const [state, setState] = useState<MembersState>({ status: 'loading' });

  const reload = useCallback(async () => {
    setState(await fetchState(projectId));
  }, [projectId]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const next = await fetchState(projectId);
      if (!cancelled) setState(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const replace = useCallback((members: readonly ProjectMember[]) => {
    setState({ status: 'ready', members });
  }, []);

  return (
    <MembersContext.Provider value={{ projectId, state, reload, replace }}>
      {children}
    </MembersContext.Provider>
  );
}

/**
 * The full state, for the one consumer that has to tell a refusal from an
 * empty team. Falls back to its own fetch when no provider covers this project.
 */
export function useProjectMembersState(projectId: string): MembersContextValue {
  const provided = useContext(MembersContext);
  const covered = provided !== null && provided.projectId === projectId;

  const [own, setOwn] = useState<MembersState>({ status: 'loading' });

  const reloadOwn = useCallback(async () => {
    setOwn(await fetchState(projectId));
  }, [projectId]);

  useEffect(() => {
    if (covered) return undefined;
    let cancelled = false;
    void (async () => {
      const next = await fetchState(projectId);
      if (!cancelled) setOwn(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [covered, projectId]);

  const replaceOwn = useCallback((members: readonly ProjectMember[]) => {
    setOwn({ status: 'ready', members });
  }, []);

  if (covered) return provided;
  return { projectId, state: own, reload: reloadOwn, replace: replaceOwn };
}

/**
 * Just the people, for a form that wants a dropdown. Empty when the list
 * cannot be read — which is what all four copies of this did before.
 */
export function useProjectMembers(projectId: string): readonly ProjectMember[] {
  const { state } = useProjectMembersState(projectId);
  return state.status === 'ready' ? state.members : [];
}
